import React, {
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef,
} from "react";
import LoaderComponent from "../../../components/loader-component/LoaderComponent";
import { Icon, ThemedIcon } from "@me8eon/icons";
import TypingEffect from "../../../components/ai-assist-refactored/utils/typing-effect/TypingEffect";
import { fetchSustainableITReport } from "@me8eon/ai.tools.nextthink";
import "./sustainableIT.component.scss";
import { WidgetComponent } from "../widget.types";
import { NexthinkActionWidgetMeta } from "@me8eon/ai.tools.nextthink/src/model/nexthinkWidget";
import { useJwtToken } from "@me8eon/react_authentication";
import { useLocalTranslation } from "./useLocalTranslation";

export const SustainableItActionWidget: WidgetComponent = (props) => {
    const values = props.values._meta.widget as NexthinkActionWidgetMeta;
    const { devices, document, queryLanguage } = values.props;
    const [loading, setLoading] = useState(false);
    const [responseData, setResponseData] = useState<any>(null);
    const [errorContent, setErrorContent] = useState<string | null>(null);
    const jwtTokenFn = useJwtToken();
    const { localT: t } = useLocalTranslation(queryLanguage);

    const deviceId = useMemo(
        () =>
            devices && devices.length
                ? (devices[0]["device.collector.uid"] ?? null)
                : null,
        [devices]
    );

    const fetchedReportRef = useRef<Set<string>>(new Set());

    const fetchReport = useCallback(
        async (currentDeviceId: string, signal?: AbortSignal) => {
            if (
                !currentDeviceId ||
                fetchedReportRef.current.has(currentDeviceId)
            )
                return;
            setLoading(true);
            try {
                const resp = await fetchSustainableITReport(
                    currentDeviceId,
                    jwtTokenFn
                );
                if (signal?.aborted) return;

                if (!resp) {
                    setErrorContent(
                        t("chatBot.nexthinkIntegration.errorFetchingReport")
                    );
                } else {
                    setResponseData(resp);
                    fetchedReportRef.current.add(currentDeviceId);
                }
            } catch (error) {
                if (!signal?.aborted) {
                    console.error("Error fetching report:", error);
                    setErrorContent(
                        t("chatBot.nexthinkIntegration.errorFetchingReport")
                    );
                }
            } finally {
                if (!signal?.aborted) setLoading(false);
            }
        },
        [jwtTokenFn, t]
    );

    useEffect(() => {
        const controller = new AbortController();
        if (!deviceId) {
            setErrorContent(
                t("chatBot.nexthinkIntegration.errorFetchingReport")
            );
            return () => controller.abort();
        }

        fetchReport(deviceId, controller.signal);
        return () => controller.abort();
    }, [deviceId, fetchReport]);

    const ReportContent = useMemo(() => {
        return (
            <div className="report-content">
                <div className="device-info">
                    <div className="device-label">
                        {t("chatBot.nexthinkIntegration.device")}
                        <span className="icon">
                            <ThemedIcon name="deviceIcon" />
                        </span>
                    </div>
                    <div className="device-name">
                        {responseData?.deviceName}{" "}
                        <span className="virtual-tag">
                            {responseData?.hardwareType}
                        </span>
                    </div>
                    <div className="uptime">
                        <div className="restart-days">
                            <Icon
                                name={
                                    responseData?.lastBootRating === "poor"
                                        ? "bad_rating"
                                        : "tick_small"
                                }
                                themedColor={
                                    responseData?.lastBootRating === "poor"
                                        ? "#EA1B0A"
                                        : "#00B876"
                                }
                            />
                            <span>
                                {" "}
                                {responseData?.daysSinceLastBoot}{" "}
                                {t("chatBot.nexthinkIntegration.days")}
                            </span>
                        </div>
                        <span className="virtual-tag">
                            {t("chatBot.nexthinkIntegration.sinceRestarted")}
                        </span>
                        <span className="recommendation">
                            <Icon
                                name={
                                    responseData?.lastBootRating === "poor"
                                        ? "bulb_green"
                                        : "thumb_up"
                                }
                                themedColor="#34C759"
                            />
                            <span>
                                {responseData?.lastBootRating === "poor"
                                    ? t(
                                          "chatBot.nexthinkIntegration.deviceRestartRecommendation"
                                      )
                                    : t(
                                          "chatBot.nexthinkIntegration.deviceInCompliance"
                                      )}
                            </span>
                        </span>
                    </div>
                </div>
                <div className="energy-info">
                    <div className="energy-consumed">
                        <div className="device-label">
                            {t("chatBot.nexthinkIntegration.energyConsumed")}
                            <span className="icon">
                                <ThemedIcon name="bulb" />
                            </span>
                        </div>
                        <div>
                            <span className="value">
                                {responseData?.totalEnergyConsumption != null
                                    ? (
                                          responseData.totalEnergyConsumption /
                                          1000
                                      ).toFixed(2)
                                    : ""}
                            </span>
                            <span className="unit"> kWh</span>
                        </div>
                    </div>
                    <div className="energy-emitted">
                        <div className="device-label">
                            {t("chatBot.nexthinkIntegration.coEmitted")}
                            <span className="icon">
                                <ThemedIcon name="co2" />
                            </span>
                        </div>
                        <div>
                            <span className="value">
                                {responseData?.totalCO2Emitted?.toFixed(3) ??
                                    ""}{" "}
                            </span>
                            <span className="unit">gms</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }, [responseData, t]);

    const SustainableReport = useMemo(() => {
        return (
            <div className="bot-reply-container">
                {loading ? (
                    <LoaderComponent isLoading={loading} isLinear />
                ) : (
                    <>
                        <div className="title">
                            {t("chatBot.nexthinkIntegration.itReportTitle")}
                        </div>
                        {ReportContent}
                        <div className="compliance">
                            <TypingEffect
                                className="bot-text"
                                text={`${document.response}<br/><br/><b>Source: Nexthink</b>`}
                                speed={1}
                            />
                        </div>
                    </>
                )}
            </div>
        );
    }, [loading, ReportContent, document.response, t]);

    return (
        <div className="bot-header">
            {errorContent ? errorContent : SustainableReport}
        </div>
    );
};
